/**
 * Intercepteur fetch global pour gérer les erreurs d'authentification (401)
 * Redirige automatiquement vers /login en cas de token expiré ou invalide
 * SAUF si l'utilisateur est déjà sur une page publique (login, register, etc.)
 */

let isRedirecting = false;

// Pages où on ne doit PAS rediriger en cas de 401
const publicPaths = ['/login', '/register', '/reset-password', '/forgot-password', '/verify-email'];

/**
 * Vérifie si le chemin actuel est une page publique
 */
const isPublicPath = (): boolean => {
  const currentPath = window.location.pathname;
  return publicPaths.some(path => currentPath.startsWith(path));
};

/**
 * Wrapper autour de fetch pour intercepter les réponses 401
 * et rediriger automatiquement vers la page de login
 */
export const setupFetchInterceptor = () => {
  const originalFetch = window.fetch;

  window.fetch = async (...args) => {
    try {
      const response = await originalFetch(...args);

      // Si la réponse est 401 (Unauthorized)
      // Ne pas rediriger si on est déjà sur une page publique (login, register, etc.)
      if (response.status === 401 && !isRedirecting && !isPublicPath()) {
        isRedirecting = true;

        // Nettoyer le localStorage
        localStorage.removeItem("user");
        localStorage.removeItem("token");

        // Rediriger vers la page de login
        console.warn("Token expiré ou invalide. Redirection vers /login...");
        window.location.href = "/login";

        // Réinitialiser le flag après un court délai
        setTimeout(() => {
          isRedirecting = false;
        }, 1000);
      }

      return response;
    } catch (error) {
      throw error;
    }
  };
};

/**
 * Helper pour faire des requêtes avec gestion automatique des erreurs d'authentification
 * @param url URL de la requête
 * @param options Options fetch standard
 * @returns Promise<Response>
 */
export const authenticatedFetch = async (
  url: string,
  options: RequestInit = {}
): Promise<Response> => {
  const token = localStorage.getItem("token");

  const headers = {
    "Content-Type": "application/json",
    Accept: "application/json",
    ...(token && { Authorization: `Bearer ${token}` }),
    ...(options.headers || {}),
  };

  const response = await fetch(url, {
    ...options,
    headers,
  });

  // Si 401, l'intercepteur global gérera la redirection
  if (response.status === 401) {
    throw new Error("Unauthorized - Token expired or invalid");
  }

  return response;
};
